import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  startAfter,
  limit,
  getCountFromServer,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import moment from "moment-timezone"; // Import moment-timezone for timezone handling

const useTableFunction = ({
  collectionPath,
  defaultOrderBy,
  dynamicCondition,
  conditionOrderBy,
}) => {
  const [tableData, setTableData] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalLength, setTotalLength] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [searchData, setSearchData] = useState({ column: "", query: "" });
  const [searchDateRange, setSearchDateRange] = useState({
    fromDate: "",
    toDate: "",
    column: "",
  });
  const [orderByField, setOrderByField] = useState({
    column: "",
    direction: "",
  });
  const [lastDocument, setLastDocument] = useState(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchData, pageSize, orderByField, searchDateRange]);

const fetchData = async () => {
  console.log("🚀 fetchData called");
  console.log("searchData:", searchData);
  console.log("searchDateRange:", searchDateRange);
  console.log("orderByField:", orderByField);
  console.log("currentPage:", currentPage);

  try {
    setIsTableLoading(true);

    const collectionRef = collection(db, collectionPath);
    let baseQuery = collectionRef;
    const filters = [];

    // ✅ Apply dynamic condition
    if (dynamicCondition) {
      const condition = await dynamicCondition();
      if (condition && condition.column && condition.query !== undefined) {
        filters.push(where(condition.column, condition.operator, condition.query));
      }
    }

    // ✅ Apply search filter (except for document ID)
    const isDocIdSearch = searchData.column === "id";
    if (searchData.column && searchData.query && !isDocIdSearch) {
      filters.push(where(searchData.column, "==", searchData.query));
    }

    // ✅ Apply date range filter
    if (searchDateRange.fromDate && searchDateRange.toDate && searchDateRange.column) {
      const fromDate = moment.tz(searchDateRange.fromDate, "Asia/Kolkata").startOf("day").toDate();
      const toDate = moment.tz(searchDateRange.toDate, "Asia/Kolkata").endOf("day").toDate();

      console.log("🗓️ Date Range Filter:");
      console.log("From (12:00 AM IST):", fromDate);
      console.log("To (11:59:59 PM IST):", toDate);
      console.log("On Column:", searchDateRange.column);

      filters.push(where(searchDateRange.column, ">=", fromDate));
      filters.push(where(searchDateRange.column, "<=", toDate));
    }

    // ✅ Build base query
    if (filters.length > 0) {
      baseQuery = query(baseQuery, ...filters);
    }

    // ✅ Handle ordering
    if (orderByField.column && orderByField.direction) {
      console.log("📊 Sorting by user input:", orderByField);
      baseQuery = query(baseQuery, orderBy(orderByField.column, orderByField.direction));
    } else if (defaultOrderBy?.field) {
      console.log("📊 Sorting by default:", defaultOrderBy);
      baseQuery = query(baseQuery, orderBy(defaultOrderBy.field, defaultOrderBy.direction || "asc"));
    }

    // ✅ Count documents (if not searching by doc ID)
    if (!isDocIdSearch) {
      const countSnapshot = await getCountFromServer(baseQuery);
      setTotalLength(countSnapshot.data().count);
    }

    // ✅ Handle pagination
    const paginatedQuery =
      currentPage > 0 && lastDocument
        ? query(baseQuery, startAfter(lastDocument), limit(pageSize))
        : query(baseQuery, limit(pageSize));

    console.log("📥 Getting documents (paginated):", {
      currentPage,
      pageSize,
      lastDocument,
    });

    const snapshot = await getDocs(paginatedQuery);
    let docs = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

    // ✅ Apply manual doc ID filtering (after fetch)
    if (isDocIdSearch && searchData.query) {
      docs = docs.filter(doc => doc.id === searchData.query);
      setTotalLength(docs.length); // set count manually
    }

    setLastDocument(snapshot.docs[snapshot.docs.length - 1] || null);
    setTableData(docs);
    setIsTableLoading(false);
  } catch (error) {
    console.error("🔥 fetchData error:", error);
    setIsTableLoading(false);
  }
};

  const refetchTableData = () => {
    fetchData();
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchQuery = (column = "", query = "") => {
    if (
      searchData.query !== query ||
      (searchData.query && searchData.column !== column)
    ) {
      setSearchData({ column, query });
    }
    console.log(column,query)

  };

  const handlePageSize = (pageSize) => {
    setCurrentPage(0);
    setPageSize(pageSize);
  };

  const handleTableSorting = (column, direction) => {
    setOrderByField({ column, direction });
  };

  const handleSearchDate = (fromDate, toDate, searchColumn) => {
    setCurrentPage(0);
    setSearchDateRange({
      fromDate,
      toDate,
      column: searchColumn,
    });
  };

  return {
    tableData,
    isTableLoading,
    currentPage,
    totalLength,
    handleSearchQuery,
    handlePageChange,
    handlePageSize,
    handleTableSorting,
    handleSearchDate,
    refetchTableData,
  };
};

export default useTableFunction;


  // Assuming collectionRef is already defined and points to a Firestore collection
// const baseQuery = query(
//   collectionRef,
//   where(condition.column, condition.operator, condition.query),
//   where(searchData.column, "==", searchData.query)
// );

// Fetch the query results
//const querySnapshot =  getDocs(baseQuery);

// Map over the snapshot to get an array of document data
//const allData = querySnapshot.docs.map(doc => doc.data());

//console.log(allData);  // This will contain all the documents that match your query